import {debounce} from 'lodash-es'
import Siema from 'siema'
import Headroom from 'headroom.js'
import {addMonths} from 'date-fns'
import scrollToElement from 'scroll-to-element'

import * as storage from './storage.jsx'
import {isValidOption, getFrontendUrl} from './helpers.jsx'
import langPopup from './langPopup.json'
import { loadLanguages, loadSubjects, loadLanguagesSync, loadSubjectsSync } from './dataLoader.jsx'
import {externalFAQRoute, getLinkToOrder} from "../helpers/externalRoutes.jsx"

export const getOptionsFromQuery = (selectOptionsQuery) => {
  const options = document.querySelectorAll(`${selectOptionsQuery} option`)

  if (options.length) {
    return Array.prototype.map.call(options, (option) => ({
      id: option.value,
      name: option.text,
    }))
  } else return []
}

export const getPreselectedOption = (selectOptionsQuery) => {
  const selectedOption = document.querySelector(
    `${selectOptionsQuery} option:checked`,
  )

  if (selectedOption instanceof HTMLOptionElement) {
    return {
      id: selectedOption.value,
      name: selectedOption.text,
    }
  } else {
    return undefined
  }
}

export const getInstantQuoteLanguages = () => {
  return loadLanguagesSync()
}

export const getInstantQuoteLanguagesAsync = async () => {
  return loadLanguages()
}

export const getCountries = () => {
  return getOptionsFromQuery('#Countries')
}

export const getInstantQuoteSubjects = () => {
  return loadSubjectsSync()
}

export const getInstantQuoteSubjectsAsync = async () => {
  return await loadSubjects()
}

export const getAndUpdateStoredInstantQuotePreselectedSourceLanguage = (forceUseDefaultValues) => {
  const querySourceLanguage = getQueryParam('source')
  const querySourceLanguageOption = getInstantQuoteLanguages().filter(function (language) {
    return language.id === querySourceLanguage
  })[0]
  const storedSourceLanguageOption = storage.getInstantQuoteSourceLanguage()

  if (forceUseDefaultValues)
    return getPreselectedOption('#InstantQuoteSourceLanguages')
  else if (querySourceLanguage && querySourceLanguageOption) {
    storage.storeInstantQuoteSourceLanguage(querySourceLanguageOption)
    return querySourceLanguageOption
  } else if (storedSourceLanguageOption) return storedSourceLanguageOption
  else return getPreselectedOption('#InstantQuoteSourceLanguages')
}

export const getBrowserLanguage = () => {
  const browserLanguage = window.navigator.language
  const languageOption = getInstantQuoteLanguages().filter(function (language) {
    return language.id === browserLanguage
  })[0]

  return languageOption
}

export const getAndUpdateStoredInstantQuotePreselectedTargetLanguages = (forceUseDefaultValues) => {
  const preselectedTargetLanguage = getPreselectedOption(
    '#InstantQuoteTargetLanguages',
  )
  const queryTargetLanguagesQuery = getQueryParam('target') || ''
  const queryTargetLanguages = queryTargetLanguagesQuery.split(',')
  const queryTargetLanguagesOptions = getInstantQuoteLanguages().filter(
    function (language) {
      return (
        isValidOption(language) &&
        queryTargetLanguages.indexOf(language.id) > -1
      )
    },
  )
  const storedTargetLanguagesOptions = storage.getInstantQuoteTargetLanguages()
  const storedValidTargetLanguagesOptions =
    storedTargetLanguagesOptions &&
    storedTargetLanguagesOptions.filter(isValidOption)

  if (forceUseDefaultValues) {
    return preselectedTargetLanguage ? [preselectedTargetLanguage] : undefined
  } else if (queryTargetLanguagesOptions.length > 0) {
    storage.storeInstantQuoteTargetLanguages(queryTargetLanguagesOptions)
    return queryTargetLanguagesOptions
  } else if (
    storedValidTargetLanguagesOptions &&
    storedValidTargetLanguagesOptions.length > 0
  ) {
    return storedValidTargetLanguagesOptions
  } else {
    return preselectedTargetLanguage ? [preselectedTargetLanguage] : undefined
  }
}

export const getAndUpdateStoredInstantQuotePreselectedSubject = (forceUseDefaultValues) => {
  const querySubject = getQueryParam('subject')
  const querySubjectOption = getInstantQuoteSubjects().filter(function (subject) {
    return subject.id === querySubject
  })[0]
  const storedSubjectOption = storage.getInstantQuoteSubject()

  if (forceUseDefaultValues) {
    return getPreselectedOption('#InstantQuoteSubjects')
  } else if (querySubjectOption) {
    storage.storeInstantQuoteSubject(querySubjectOption)
    return querySubjectOption
  } else if (storedSubjectOption) {
    return getInstantQuoteSubjects().filter(function (subject) {
      return subject.id === storedSubjectOption.id
    })[0]
  } else {
    return getPreselectedOption('#InstantQuoteSubjects')
  }
}

export const getAndUpdateStoredInstantQuotePrefilledWordCount = (
  forceUseDefaultValues,
) => {
  const queryWords = parseInt(getQueryParam('words'))
  const storedWords = storage.getInstantQuoteWords()

  if (forceUseDefaultValues) {
    const wordCount = document.querySelector('#InstantQuoteWordCount')
    return parseInt(wordCount.value) || 0
  } else if (queryWords && !isNaN(queryWords)) {
    // storage.storeInstantQuoteWords(queryWords);
    // return queryWords;
    return 1000 // logic handled in upload components
  } else if (storedWords) {
    return storedWords
  } else {
    const wordCount = document.querySelector('#InstantQuoteWordCount')
    return parseInt(wordCount.value) || 0
  }
}

export const getContactFormSubjects = () => {
  return getOptionsFromQuery('#ContactFormSubject')
}

export const getContactFormPreselectedSubject = () => {
  return getPreselectedOption('#ContactFormSubject')
}

export const hideHtmlStaticContactForm = () => {
  const htmlContactForm = document.querySelector('#html--contact-form')
  if (htmlContactForm) htmlContactForm.style.display = 'none'
}

export const hideHtmlStaticInstantQuoteForm = () => {
  const htmlInstantQuote = document.querySelector('#html--instant-quote-app')
  if (htmlInstantQuote) htmlInstantQuote.style.display = 'none'
}

export const isElementChildOf = (element, query, maxDeepness = 10) => {
  if (!element || maxDeepness < 0) return false
  else
    return (
      element.matches(query) ||
      isElementChildOf(element.parentElement, query, maxDeepness - 1)
    )
}

export const setCookie = (name, value, durationInMonths) => {
  let expires = ''

  if (durationInMonths) {
    const expiresUTCString = addMonths(new Date(), durationInMonths).toUTCString()
    expires = `; expires=${expiresUTCString}`
  }

  document.cookie = `${name}=${value || ''}${expires}; path=/`
}

export const onChangeLanguage = (event) => {
  const nextPathname = event.target.value
  setCookie(
    'lang',
    event.target.querySelector(`[value="${nextPathname}"]`).dataset.lang,
    2,
  )
  const hash = window.location.hash
  window.location.href = `${nextPathname}${hash}`
}

export const listenChangeLanguage = () => {
  const footerLanguage = document.querySelector('#FooterLanguage')
  if (footerLanguage)
    footerLanguage.addEventListener('change', onChangeLanguage)
}

export const stopListenChangeLanguage = () => {
  const footerLanguage = document.querySelector('#FooterLanguage')
  if (footerLanguage)
    footerLanguage.removeEventListener('change', onChangeLanguage)
}

export const hideSignInButton = () => {
  // Desktop: elemento con id="signin"
  const signinById = document.querySelector('#signin')
  if (signinById) signinById.style.display = 'none'
  
  // Mobile: elemento con classname="signin"
  const signinByClass = document.querySelector('.signin')
  if (signinByClass) signinByClass.style.display = 'none'
}

export const listenSignInClick = (onClick) => {
  // Desktop: elemento con id="signin"
  const signinById = document.querySelector('#signin')
  if (signinById) signinById.addEventListener('click', onClick)
  
  // Mobile: elemento con classname="signin"
  const signinByClass = document.querySelector('.signin')
  if (signinByClass) signinByClass.addEventListener('click', onClick)
}

export const stopListeningSignInClick = (onClick) => {
  // Desktop: elemento con id="signin"
  const signinById = document.querySelector('#signin')
  if (signinById) signinById.removeEventListener('click', onClick)
  
  // Mobile: elemento con classname="signin"
  const signinByClass = document.querySelector('.signin')
  if (signinByClass) signinByClass.removeEventListener('click', onClick)
}

export const listenNotCallingFromLink = (onClick) => {
  const notCallingFromLink = document.querySelector('[data-phone-country]')
  if (notCallingFromLink) notCallingFromLink.addEventListener('click', onClick)
}

export const stopListenNotCallingFromLink = (onClick) => {
  const notCallingFromLink = document.querySelector('[data-phone-country]')
  if (notCallingFromLink)
    notCallingFromLink.removeEventListener('click', onClick)
}

export const getSelectedCurrency = () => {
  const element = document.querySelector('#FooterCurrency')
  if (element) return element.value
  return 'EUR' // Default currency for development
}

export const setSelectedCurrency = (value) => {
  const element = document.querySelector('#FooterCurrency')
  if (element) element.value = value
}

export const currencyChangeAddEventListener = (handler) => {
  const element = document.querySelector('#FooterCurrency')
  if (element) element.addEventListener('change', handler)
}

export const currencyChangeRemoveEventListener = (handler) => {
  const element = document.querySelector('#FooterCurrency')
  if (element) element.removeEventListener('change', handler)
}

// tofix: use classlist
export const addPreventScrollUnderModal = () => {
  const body = document.querySelector('body')
  if (body) body.className = `${body.className} is-modal-visible`
}

export const removePreventScrollUnderModal = () => {
  const body = document.querySelector('body')
  if (body)
    body.className = `${body.className.replace(/is-modal-visible/g, '')} `
}

export const navigateToOrder = () =>
  window.location.replace(`${getLinkToOrder()}${window.location.hash || ''}`)

export const getLinkToPayAfterDeliveryInfo = () => {
  const currentLang = (document.documentElement.lang || 'en').split('-')[0]
  const baseUrl = getFrontendUrl()
  const faqPath = externalFAQRoute[currentLang] || externalFAQRoute['en']
  return `${baseUrl}${faqPath}`
}

export const getPhoneNumbers = () => window.phoneNumbers || []

export const scrollToTop = () =>
  (document.body.scrollTop = document.documentElement.scrollTop = 0)

export const scrollToFirstInvalidInput = () =>
  scrollToElement('.input--is-invalid', {
    offset: -64,
    ease: 'in-out-sine',
    duration: 800,
  })

//tofix: test on ie11
export const existsQueryParam = (param) => {
  const params = Object.fromEntries(new URLSearchParams(window.location.search))
  return !!params[param]
}

export const getQueryParam = (param) => {
  const params = Object.fromEntries(new URLSearchParams(window.location.search))
  return params[param]
}

const onSiemaSliderChange = function () {
  const siemaButtons = this.selector.getElementsByTagName('button')
  const counter = this.currentSlide
  const slides = document.querySelectorAll('.slider > div > div')
  const slidewrap = document.querySelector('.slider > div')
  if (slidewrap) {
    slidewrap.style.height = slides[counter + 1].offsetHeight + 'px'
  }

  Array.prototype.forEach.call(siemaButtons, (siemaButton, index) => {
    if (index === this.currentSlide) {
      siemaButtons[index].classList.add('active')
    } else {
      siemaButtons[index].classList.remove('active')
    }
  })
}

export const initSliders = () => {
  const sliderElements = document.getElementsByClassName('slider')

  if (sliderElements.length > 0) {
    const sliders = new Siema({
      selector: '.slider',
      duration: 250,
      easing: 'ease-out',
      perPage: 1,
      startIndex: 0,
      draggable: true,
      multipleDrag: false,
      threshold: 20,
      loop: true,
      rtl: false,
      onChange: onSiemaSliderChange,
    })

    const slides = document.querySelectorAll('.slider > div > div')
    const slidewrap = document.querySelector('.slider > div')
    let slidedrag = false
    if (slidewrap) {
      slidewrap.style.height = slides[1].offsetHeight + 'px'
      slidewrap.addEventListener('mousedown', () => {
        slidedrag = false
      })
      slidewrap.addEventListener('mousemove', () => {
        slidedrag = true
      })
      slidewrap.addEventListener('mouseup', () => {
        if (!slidedrag) {
          sliders.next()
        }
      })
    }

    Siema.prototype.addPagination = function () {
      Array.prototype.forEach.call(
        this.innerElements,
        (innerElement, index) => {
          const siemaPaginationButton = document.createElement('button')
          siemaPaginationButton.textContent = `${index}`
          siemaPaginationButton.addEventListener('click', () => {
            this.goTo(index)
          })
          if (index === 0) siemaPaginationButton.classList.add('active')
          this.selector.appendChild(siemaPaginationButton)
        },
      )
    }

    sliders.addPagination()

    const addPagination = function () {
      sliders.addPagination()
    }

    window.addEventListener('resize', debounce(addPagination, 250))
  }
}

export const initAccordion = () => {
  const accordionElements = document.getElementsByClassName('accordion__title')
  const accordionClass = 'accordion__title--collapsed'

  const collapseSection = (element) => {
    const sectionHeight = element.scrollHeight
    const elementTransition = element.style.transition
    element.style.transition = ''
    requestAnimationFrame(() => {
      element.style.height = sectionHeight + 'px'
      element.style.transition = elementTransition
      requestAnimationFrame(() => (element.style.height = 0 + 'px'))
    })
    element.previousElementSibling.classList.add(accordionClass)
  }

  const expandSection = (element) => {
    const sectionHeight = element.scrollHeight
    element.style.height = sectionHeight + 'px'
    element.addEventListener('transitionend', function tEnd() {
      element.removeEventListener('transitionend', tEnd)
      element.style.height = null
    })
    element.previousElementSibling.classList.remove(accordionClass)
  }

  Array.prototype.forEach.call(accordionElements, function (el) {
    el.addEventListener('click', function () {
      let section = el.nextElementSibling
      if (el.classList.contains(accordionClass)) {
        expandSection(section)
        el.classList.remove(accordionClass)
      } else {
        collapseSection(section)
      }
    })
  })
}

export const simpleModal = (
  title = '',
  message = '',
  icon = '',
  isError = false,
) => {
  let text = ''
  let responsetime = document.querySelector('.headliner h5')

  if (icon) text = '<i class="icon icon--modal icon--' + icon + '"></i>'

  if (title) text = text + '<h4>' + title + '</h4>'

  if (!isError && responsetime) text = text + responsetime.textContent + ' '

  if (message) text = text + message

  const modal =
    '<div class="modal modal--small modal--flash"><div class="modal__body"><div class="modal__content"><div class="icon modal__header-close--always-visible"></div>' +
    text +
    '</div></div></div>'

  document.querySelector('body').classList.add('is-modal-visible')
  document.querySelector('body').insertAdjacentHTML('beforeend', modal)
  document
    .querySelector('.modal--flash')
    .addEventListener('click', function (el) {
      el.preventDefault()
      let modal = document.querySelector('.modal--flash')
      modal.parentNode.removeChild(modal)
      document.querySelector('body').classList.remove('is-modal-visible')
    })
}

export let snackbarTimer

export const showSnackbar = (message, kind) => {
  clearTimeout(snackbarTimer)

  const snackbarDomElement = document.querySelector('#snackbar')
  const snackbarCopyDomElement = document.querySelector(
    '#snackbar .snackbar__copy',
  )
  const snackbarCloseWrapperDomElement = document.querySelector(
    '#snackbar .snackbar__close-wrapper',
  )

  if (
    snackbarDomElement &&
    snackbarCopyDomElement &&
    snackbarCloseWrapperDomElement
  ) {
    snackbarCopyDomElement.innerHTML = message
    snackbarCloseWrapperDomElement.addEventListener('click', hideSnackbar)
    snackbarTimer = setTimeout(hideSnackbar, 10 * 1000)
    snackbarDomElement.className = `snackbar snackbar--visible snackbar--${kind}`
  }
}

export const hideSnackbar = () => {
  clearTimeout(snackbarTimer)

  const snackbarDomElement = document.querySelector('#snackbar')
  const snackbarCopyDomElement = document.querySelector(
    '#snackbar .snackbar__copy',
  )
  const snackbarCloseWrapperDomElement = document.querySelector(
    '#snackbar .snackbar__close-wrapper',
  )

  if (
    snackbarDomElement &&
    snackbarCopyDomElement &&
    snackbarCloseWrapperDomElement
  ) {
    snackbarDomElement.className = 'snackbar'
    snackbarCopyDomElement.innerText = ''
    snackbarCloseWrapperDomElement.removeEventListener('click', hideSnackbar)
  }
}

export const toggleHeader = () => {
  const header = document.querySelector('header')
  const hamburger = document.querySelector('#input-hamburger')

  if (header && hamburger) {
    hamburger.addEventListener(
      'change',
      function () {
        if (this.checked) header.classList.add('header--menu-open')
        else header.classList.remove('header--menu-open')
      },
      false,
    )

    const headroom = new Headroom(header, {
      offset: 100,
      tolerance: {
        up: 5,
        down: 0,
      },
      classes: {
        initial: 'header',
        pinned: 'header--pinned',
        unpinned: 'header--unpinned',
        top: 'header--top',
        notTop: 'header--not-top',
        bottom: 'header--bottom',
        notBottom: 'header--not-bottom',
      },
    })

    headroom.init()
  }
}

export const mobileResizeUpdateModalContentHeight = () => {
  if (window.innerWidth <= 768) {
    const modalContent = document.querySelector(
      '.modal .modal__body .modal__content',
    )
    const modalHeader = document.querySelector('.modal .modal__header')
    const modalFooter = document.querySelector('.modal .modal__footer')
    if (modalContent) {
      const modalHeaderHeight = modalHeader?.offsetHeight ?? 0
      const modalFooterHeight = modalFooter?.offsetHeight ?? 0
      const gap = window.innerHeight * 0.15
      const modalContentHeight =
        window.innerHeight - gap - modalHeaderHeight - modalFooterHeight
      modalContent.style = `height: ${modalContentHeight}px;`
    }
  }
}

const debouncedMobileResizeUpdateModalContentHeight = debounce(
  mobileResizeUpdateModalContentHeight,
  500,
)

export const startMobileListenResizeForUpdateModalContentHeight = () =>
  window.addEventListener(
    'resize',
    debouncedMobileResizeUpdateModalContentHeight,
  )

export const stopMobileListenResizeForUpdateModalContentHeight = () =>
  window.addEventListener(
    'resize',
    debouncedMobileResizeUpdateModalContentHeight,
  )

export const tableToggle = () => {
  const tabletoggleElements = document.querySelectorAll('table.advanced tr[id]')
  const tabletoggleClass = 'expanded'

  Array.prototype.forEach.call(tabletoggleElements, (el) => {
    el.addEventListener('click', () => {
      if (el.classList.contains(tabletoggleClass))
        el.classList.remove(tabletoggleClass)
      else el.classList.add(tabletoggleClass)
    })
  })
}

export const tabToggle = () => {
  const tabtoggleElements = document.querySelectorAll('a.tab-link')
  const tabtoggleClass = 'tab-link--active'
  const tabClass = 'tab--active'

  Array.prototype.forEach.call(tabtoggleElements, (el) => {
    el.addEventListener('click', (e) => {
      e.preventDefault()

      const link = el.getAttribute('href').replace('#', '')
      el.classList.add(tabtoggleClass)

      Array.prototype.forEach.call(el.parentNode.children, (child) => {
        if (child !== el) {
          if (child.classList.contains('tab-link')) {
            child.classList.remove(tabtoggleClass)
          }
          if (child.classList.contains('tab')) {
            if (child.getAttribute('id') === link) {
              child.classList.add(tabClass)
            } else {
              child.classList.remove(tabClass)
            }
          }
        }
      })

      if (el.parentNode.classList.contains('tab-wrap')) {
        Array.prototype.forEach.call(
          el.parentNode.parentNode.children,
          (child) => {
            if (child !== el) {
              if (child.classList.contains('tab')) {
                if (child.getAttribute('id') === link) {
                  child.classList.add(tabClass)
                } else {
                  child.classList.remove(tabClass)
                }
              }
            }
          },
        )
      }
    })
  })
}

export const updateCustomersCountDomElements = (customers) =>
  document
    .querySelectorAll('[data-customers-count]')
    .forEach((element) => (element.innerHTML = customers))

export const updateTranslatorsCountDomElements = (translators) =>
  document
    .querySelectorAll('[data-translators-count]')
    .forEach((element) => (element.innerHTML = translators))

export const updateLanguagesCountDomElements = (languages) =>
  document
    .querySelectorAll('[data-languages-count]')
    .forEach((element) => (element.innerHTML = languages))

export const updateDataPhoneDomElements = (phone) =>
  document.querySelectorAll('[data-phone-number]').forEach((element) => {
    element.href = 'tel:' + phone
    element.innerHTML = phone
  })

export const updateDataCallNowDomElements = (
  message,
  isOpen,
  messageOtherNumbers,
  messageAfterStatus,
) => {
  document.querySelectorAll('[data-call-now]').forEach((element) => {
    element.innerHTML = message
    element.className = isOpen
      ? 'call-now__status call-now__status--active'
      : 'call-now__status'
  })

  document
    .querySelectorAll('[data-call-now-additional-msg]')
    .forEach((element) => (element.innerHTML = messageAfterStatus))

  document
    .querySelectorAll('[data-phone-country]')
    .forEach((element) => (element.innerHTML = messageOtherNumbers))
}

export const showHeaderUserInfoDomElement = () => {
  const userInfoDomElement = document.getElementById('userInfo')
  if (userInfoDomElement)
    userInfoDomElement.className = userInfoDomElement.className.replace(
      'header__userInfo--hidden',
      '',
    )
}

export const shouldInstantQuoteUseLocalStorageParams = () =>
  !!document.querySelector('[data-use-local-storage-params="true"]')

export const removeTableLimit = () => {
  const tableLimitToggles = document.querySelectorAll('a.remove-table-limit')
  Array.prototype.forEach.call(tableLimitToggles, (el) => {
    el.addEventListener('click', (e) => {
      e.preventDefault()
      el.closest('table').classList.remove('first-10')
      el.parentNode.removeChild(el)
    })
  })
}

// tofix: refactor this
export const languageBanner = (defaultlang) => {
  let cookieLang = ''
  let ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) == ' ') {
      c = c.substring(1)
    }

    if (c.indexOf('bannerlang=') == 0) {
      cookieLang = c.substring('bannerlang='.length, c.length)
    }
  }

  if (!cookieLang) {
    let userLang = navigator.language || navigator.userLanguage
    userLang = userLang.slice(0, 2)
    if (typeof langPopup[userLang] === 'undefined') {
      userLang = defaultlang
    }
    let switchNode = document.querySelectorAll(
      '#FooterLanguage option[data-lang="' + userLang + '"]:not([selected])',
    )
    if (switchNode.length) {
      let switchLink = switchNode[0].getAttribute('value')
      document
        .querySelectorAll('body > footer')[0]
        .insertAdjacentHTML(
          'beforeend',
          '<aside class="language-redirect language-redirect--' +
            userLang +
            '"><a href="#">&times;</a><h6>' +
            langPopup[userLang][0] +
            '</h6><a href="' +
            switchLink +
            '" class="link">' +
            langPopup[userLang][1] +
            '</a> <a href="#">' +
            langPopup[userLang][2] +
            '</a></aside>',
        )
      ;[].forEach.call(document.querySelectorAll('aside a'), function (el) {
        el.addEventListener('click', function (el) {
          if (el.currentTarget.href.includes('#')) {
            el.preventDefault()
            let d = new Date()
            d.setTime(d.getTime() + 5184000000)
            let expires = 'expires=' + d.toUTCString()
            document.cookie =
              'bannerlang=' + userLang + ';' + expires + ';path=/'
            document.querySelectorAll(
              'aside.language-redirect',
            )[0].style.display = 'none'
          }
        })
      })
    }
  }
}

export const tindex = () => {
  if (document.getElementById('tindex-view-full-list')) {
    document.getElementById('tindex-view-full-list').onclick = function () {
      document.getElementById('tindex-table').scrollIntoView()
      return false
    }
  }
}

